import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import Checkbox from '../checkbox';
import RichTextBase from '../../rich-text/base';
import { RichTextPropTypes } from '../../../prop-types';

import { Wrapper } from './styles';

const CheckboxGroup = forwardRef(
  (
    {
      error,
      options = [],
      name,
      values,
      id,
      inline,
      value: groupValue,
      legend,
      description,
      isControlled = false,
      ...rest
    },
    ref
  ) => {
    const checkboxRefs = useRef([]);
    const wrapperRef = useRef([]);

    const [isRequired, setIsRequired] = useState(rest.required);

    useImperativeHandle(ref, () => ({
      get wrapper() {
        return wrapperRef.current;
      },
      get checkboxes() {
        return checkboxRefs.current;
      },
    }));

    const isValuesArray = values && Array.isArray(values);

    const getIsChecked = useCallback(
      (value) => (isValuesArray ? values.includes(value) : values),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [values]
    );

    const isAtLeastOneChecked = () =>
      checkboxRefs.current.some((checkbox) => checkbox?.checked);

    useEffect(() => {
      if (rest.required) {
        setIsRequired(!isAtLeastOneChecked());
      }
    }, [values, rest.required]);

    return (
      <Wrapper $inline={inline} ref={wrapperRef}>
        {legend && legend}
        {description && <RichTextBase richText={description} />}
        {options
          ? options.map(({ value, label, reversed }, i) => {
              const isChecked = getIsChecked(value);

              return (
                <Checkbox
                  {...rest}
                  ref={(checkboxRef) => {
                    checkboxRefs.current[i] = checkboxRef;
                  }}
                  name={name}
                  key={`${id || name}-${i}`}
                  id={`${id || name}-${i}`}
                  label={label}
                  reversed={reversed}
                  checked={isControlled ? isChecked : undefined}
                  defaultChecked={isChecked}
                  value={value}
                  required={isRequired}
                />
              );
            })
          : null}
      </Wrapper>
    );
  }
);

CheckboxGroup.displayName = 'CheckboxGroup';

CheckboxGroup.propTypes = {
  error: PropTypes.bool,
  name: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  values: PropTypes.oneOf(PropTypes.arrayOf(PropTypes.string), PropTypes.bool),
  value: PropTypes.arrayOf(PropTypes.string),
  inline: PropTypes.bool,
  isControlled: PropTypes.bool,
  id: PropTypes.string,
  legend: PropTypes.node,
  description: RichTextPropTypes,
};

export default CheckboxGroup;
